import {ReactNode} from 'react';
import NextLink from 'next/link';
import {useIntl} from 'react-intl';
import {UrlObject} from 'url';
import {Box, BoxProps, Flex, Link, Text} from '@chakra-ui/react';
import dayjs from 'dayjs';

import {useTranslate} from '@eksab/i18n';
import {BadgeIcon, CalendarIcon, ClockIcon, LeftChevronIcon} from '@eksab/assets/icons';

import {Match} from '../components';

type Props = {
  match: MatchT;
  userPoints?: number | null;
  href?: UrlObject;
  underScoreNode?: ReactNode;
} & BoxProps;

export function MatchCard({match, userPoints, href, onClick, underScoreNode, ...props}: Props) {
  const isFantasyMatch = !!userPoints && userPoints > 0 && !!href;

  return (
    <Box
      display="flex"
      flexDir={['column', null, 'row']}
      rounded="md"
      shadow="md"
      bgColor="white"
      minH="100"
      maxW="48.75rem"
      onClick={onClick}
      cursor={!href || userPoints ? 'pointer' : 'initial'}
      {...props}
    >
      <Box
        bgColor="gray.600"
        h={[10, null, 'unset']}
        minW="11.25rem"
        pos="relative"
        display="flex"
        justifyContent={['space-around', null, 'center']}
        alignItems="center"
        flexDir={['row', null, 'column']}
      >
        <Box display="flex" flexDir={['row', null, 'column']} gap={['5', null, 'unset']}>
          {userPoints && userPoints > 0 ? (
            <WonPoints userPoints={userPoints} />
          ) : (
            <MatchDate isLive={match.state.code === 'LIVE'} matchStart={match.start} />
          )}
        </Box>

        {isFantasyMatch && (
          <NextLink href={href} passHref legacyBehavior>
            <Link color="gray.500" display={{md: 'none'}} pos="absolute" insetEnd={5} fontSize={['xs', null, 'sm']}>
              Details
              <LeftChevronIcon transform="auto" scaleX="-1" ms="1.5" width="1" height="2" color="gray.500" />
            </Link>
          </NextLink>
        )}
      </Box>
      <Box display="flex" flex="1" pos="relative" flexDir="column" px={[3, null, 12]} py="5" justifyContent="center">
        <Match match={match} underScoreNode={underScoreNode} />
        {underScoreNode}
        {isFantasyMatch && (
          <NextLink href={href} passHref legacyBehavior>
            <Link display={['none', null, 'unset']} pos="absolute" top="50%" insetEnd={5} transform="translateY(-50%)">
              <LeftChevronIcon transform="auto" scaleX="-1" width="1.5" color="gray.500" />
            </Link>
          </NextLink>
        )}
      </Box>
    </Box>
  );
}

function WonPoints({userPoints}: {userPoints: number}) {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  return (
    <Flex>
      <BadgeIcon me="2.5" />
      <Text fontSize="12" color="green.400" fontWeight="700">
        {t('competition.matches.won_points', {pts: format(userPoints, {minimumFractionDigits: 2})})}
      </Text>
    </Flex>
  );
}

function MatchDate({matchStart, isLive}: {matchStart: string; isLive: boolean}) {
  const t = useTranslate();

  return (
    <>
      <Box display="flex" alignItems="center" gap="2" lineHeight="1.5rem">
        <CalendarIcon boxSize={4} />
        <Text fontSize={['xs', null, 'sm']}>{dayjs(matchStart).format('D MMM YYYY')}</Text>
      </Box>

      <Box display="flex" alignItems="center" gap="2" lineHeight="1.5rem">
        <ClockIcon />
        <Text fontSize={['xs', null, 'sm']} fontWeight={isLive ? 800 : 500} color={isLive ? 'green.400' : 'black'}>
          {isLive ? t('competition.matches.state.live') : dayjs(matchStart).format('HH:mm')}
        </Text>
      </Box>
    </>
  );
}
